<template>
  <!-- BaseModal은 Headless UI 포털을 자동 사용 -->
  <MemberInfoEditDialog v-if="memberInfoEditState" v-model:isOpen="memberInfoEditState" />
  <ResetPasswordDialog v-if="resetPasswordState" v-model:isOpen="resetPasswordState" />
  <LoginNeededDialog v-if="loginNeededState" v-model:isOpen="loginNeededState" />
  <AccountPasswordDialog
    v-if="accountPasswordState"
    v-model:isOpen="accountPasswordState"
    @close="handleAccountPasswordClose"
    @confirm="handleAccountPasswordConfirm"
  />
  <AccountPasswordSetupDialog
    v-if="accountPasswordSetupState"
    v-model:isOpen="accountPasswordSetupState"
    @close="handleAccountPasswordSetupClose"
    @confirm="handleAccountPasswordSetupConfirm"
  />
  <AccountPasswordInputDialog
    v-if="accountPasswordInputState"
    v-model:isOpen="accountPasswordInputState"
    :is-opne-api="accountPasswordInputOptions?.isOpenApi ?? false"
    @close="handleAccountPasswordInputClose"
    @confirm="handleAccountPasswordInputConfirm"
  />
  <AccountPasswordResetInputDialog
    v-if="accountPasswordResetInputState"
    v-model:isOpen="accountPasswordResetInputState"
    @close="handleAccountPasswordResetInputClose"
    @confirm="handleAccountPasswordResetInputConfirm"
  />
  <AccountPasswordResetConfirmDialog
    v-if="accountPasswordResetConfirmState"
    v-model:isOpen="accountPasswordResetConfirmState"
  />
  <AccountPasswordFailExeed
    v-if="accountPasswordFailExceedState"
    v-model:isOpen="accountPasswordFailExceedState"
  />
  <AccountPasswordResetMissmatch
    v-if="accountPasswordResetMismatchState"
    v-model:isOpen="accountPasswordResetMismatchState"
  />
  <AccountPasswordResetComplete
    v-if="accountPasswordResetCompleteState"
    v-model:isOpen="accountPasswordResetCompleteState"
  />
  <AccountCreateLeave
    v-if="accountCreateLeaveState"
    v-model:isOpen="accountCreateLeaveState"
    @confirm="handleAccountCreateLeaveConfirm"
  />
  <OpenApiLoginNeededDialog
    v-if="openApiLoginNeededState"
    v-model:isOpen="openApiLoginNeededState"
    @close="handleOpenApiLoginNeededClose"
    @confirm="handleOpenApiLoginNeededConfirm"
  />
  <OpenApiAccountNeededDialog
    v-if="openApiAccountNeededState"
    v-model:isOpen="openApiAccountNeededState"
    @close="handleOpenApiAccountNeededClose"
    @confirm="handleOpenApiAccountNeededConfirm"
  />
  <DocumentSubmissionGuideDialog
    v-if="documentSubmissionGuideState"
    v-model:isOpen="documentSubmissionGuideState"
    @close="handleDocumentSubmissionGuideClose"
    @submitDocuments="handleDocumentSubmissionGuideSubmitDocuments"
  />
  <RequiredDocumentsDialog
    v-if="requiredDocumentsVisible"
    :is-visible="requiredDocumentsVisible"
    :type="requiredDocumentsType"
    @close="requiredDocumentsVisible = false"
    @confirm="handleRequiredDocumentsConfirm"
  />
  <CreateAccountDialog
    v-if="createAccountState"
    v-model:isOpen="createAccountState"
    @close="handleCreateAccountClose"
    @createAccount="handleCreateAccountConfirm"
  />
</template>

<script setup lang="ts">
import AccountPasswordResetConfirmDialog from '@/components/common/dialog/accountPassword/AccountPasswordResetConfirmDialog.vue';
import AccountPasswordResetInputDialog from '@/components/common/dialog/accountPassword/AccountPasswordResetInputDialog.vue';
import AccountPasswordResetMissmatch from '@/components/common/dialog/accountPassword/AccountPasswordResetMissmatch.vue';
import AccountPasswordResetComplete from '@/components/common/dialog/accountPassword/AccountPasswordResetComplete.vue';
import AccountPasswordSetupDialog from '@/components/common/dialog/accountPassword/AccountPasswordSetupDialog.vue';
import AccountPasswordInputDialog from '@/components/common/dialog/accountPassword/AccountPasswordInputDialog.vue';
import AccountPasswordFailExeed from '@/components/common/dialog/accountPassword/AccountPasswordFailExeed.vue';
import DocumentSubmissionGuideDialog from '@/components/auth/login/dialog/DocumentSubmissionGuideDialog.vue';
import OpenApiAccountNeededDialog from '@/components/mypage/openApi/dialog/OpenApiAccountNeededDialog.vue';
import AccountPasswordDialog from '@/components/common/dialog/accountPassword/AccountPasswordDialog.vue';
import OpenApiLoginNeededDialog from '@/components/mypage/openApi/dialog/OpenApiLoginNeededDialog.vue';
import ResetPasswordDialog from '@/components/common/dialog/password/ResetPasswordDialog.vue';
import RequiredDocumentsDialog from '@/components/auth/signup/dialog/RequiredDocumentsDialog.vue';
import CreateAccountDialog from '@/components/auth/login/dialog/CreateAccountDialog.vue';
import MemberInfoEditDialog from '@/components/common/dialog/MemberInfoEditDialog.vue';
import AccountCreateLeave from '@/components/common/dialog/AccountCreateLeave.vue';
import LoginNeededDialog from '@/components/common/dialog/LoginNeededDialog.vue';
import { LocalStorageKey, LocalStorageService, sha256 } from '@template/utils';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { Logout } from '@/services/auth.service';
import { useRouter } from 'vue-router';
import { computed, ref } from 'vue';

const dialogManager = useGlobalDialogManager();
const router = useRouter();

/**
 * 각 다이얼로그의 상태를 v-model과 호환되도록 computed로 관리
 * 새로운 다이얼로그를 추가할 때 여기에 상태를 추가하세요
 */
const memberInfoEditState = dialogManager.getDialogState('memberInfoEdit');
const resetPasswordState = dialogManager.getDialogState('resetPassword');
const loginNeededState = dialogManager.getDialogState('loginNeeded');
const accountPasswordState = dialogManager.getDialogState('accountPassword');
const accountPasswordSetupState = dialogManager.getDialogState('accountPasswordSetup');
const accountPasswordInputState = dialogManager.getDialogState('accountPasswordInput');
const documentSubmissionGuideState = dialogManager.getDialogState('documentSubmissionGuide');
const createAccountState = dialogManager.getDialogState('createAccount');
const accountPasswordInputOptions = computed(
  () =>
    (dialogManager.dialogStates.value.accountPasswordInput as { options?: { isOpenApi?: boolean } })
      .options
);
const accountPasswordResetInputState = dialogManager.getDialogState('accountPasswordResetInput');
const accountPasswordResetConfirmState = dialogManager.getDialogState(
  'accountPasswordResetConfirm'
);
const accountPasswordFailExceedState = dialogManager.getDialogState('accountPasswordFailExceed');
const accountPasswordResetMismatchState = dialogManager.getDialogState(
  'accountPasswordResetMismatch'
);
const accountPasswordResetCompleteState = dialogManager.getDialogState(
  'accountPasswordResetComplete'
);
const accountCreateLeaveState = dialogManager.getDialogState('accountCreateLeave');
const openApiLoginNeededState = dialogManager.getDialogState('openApiLoginNeeded');
const openApiAccountNeededState = dialogManager.getDialogState('openApiAccountNeeded');

// DocumentSubmissionGuideDialog의 "제출하기" 클릭 후 보여줄 서류 준비 안내
const requiredDocumentsVisible = ref(false);
const requiredDocumentsType = computed(() => {
  const userType = LocalStorageService.getItem(LocalStorageKey.MEMBER_TYPE);
  return userType === 'INDIVIDUAL' ? 'individual' : 'corporation';
});

/**
 * 계좌 비밀번호 다이얼로그 닫기 핸들러
 */
const handleAccountPasswordClose = () => {
  dialogManager.close('accountPassword');
};

/**
 * 계좌 비밀번호 다이얼로그 확인 핸들러
 * @param quickAuthEnabled - 동의 여부
 * @param password - 입력된 비밀번호
 */
const handleAccountPasswordConfirm = async (quickAuthEnabled: boolean, password: string) => {
  const encryptedPassword = await sha256(password);
  dialogManager.resolve(
    'accountPassword',
    { quickAuthEnabled, password: encryptedPassword },
    false
  );
};

/**
 * 계좌 비밀번호 설정 다이얼로그 닫기 핸들러
 */
const handleAccountPasswordSetupClose = () => {
  dialogManager.close('accountPasswordSetup');
};

/**
 * 계좌 비밀번호 설정 다이얼로그 확인 핸들러
 * @param quickAuthEnabled - 동의 여부
 * @param password - 입력된 비밀번호
 */
const handleAccountPasswordSetupConfirm = async (quickAuthEnabled: boolean, password: string) => {
  if (password.length !== 6) {
    dialogManager.resolve('accountPasswordSetup', { quickAuthEnabled, password: '' });
  } else {
    const encryptedPassword = await sha256(password);
    dialogManager.resolve(
      'accountPasswordSetup',
      {
        quickAuthEnabled,
        password: encryptedPassword,
      },
      false
    );
  }
};

/**
 * 비밀번호 입력 다이얼로그 닫기 핸들러
 */
const handleAccountPasswordInputClose = () => {
  dialogManager.close('accountPasswordInput');
};

/**
 * 비밀번호 입력 다이얼로그 확인 핸들러
 * @param password - 입력된 비밀번호
 */
const handleAccountPasswordInputConfirm = (password: string) => {
  dialogManager.resolve('accountPasswordInput', { password });
};

/**
 * 비밀번호 재설정 입력 다이얼로그 닫기 핸들러
 */
const handleAccountPasswordResetInputClose = () => {
  dialogManager.close('accountPasswordResetInput');
};

/**
 * 비밀번호 재설정 입력 다이얼로그 확인 핸들러
 * @param data - 비밀번호와 거래번호를 포함한 객체
 */
const handleAccountPasswordResetInputConfirm = (password: string) => {
  dialogManager.resolve('accountPasswordResetInput', {
    password,
  });
};

/**
 * 계좌 개설 이탈 다이얼로그 확인/취소 핸들러
 * @param confirmed - 확인 여부 (true: 라우트 허용, false: 라우트 차단)
 */
const handleAccountCreateLeaveConfirm = (confirmed: boolean) => {
  dialogManager.resolve('accountCreateLeave', { confirmed });
};

/**
 * Open API 로그인 필요 다이얼로그 닫기 핸들러
 */
const handleOpenApiLoginNeededClose = () => {
  dialogManager.close('openApiLoginNeeded');
};

/**
 * Open API 로그인 필요 다이얼로그 확인(로그아웃) 핸들러
 */
const handleOpenApiLoginNeededConfirm = async () => {
  dialogManager.close('openApiLoginNeeded');
  await Logout();
};

/**
 * Open API 계좌 필요 다이얼로그 닫기 핸들러
 */
const handleOpenApiAccountNeededClose = () => {
  dialogManager.close('openApiAccountNeeded');
};

/**
 * 서류 제출 가이드 닫기(X 등) — close() 시점에 지연된 계좌 비밀번호 셋업이 있으면 연다
 */
const handleDocumentSubmissionGuideClose = () => {
  dialogManager.close('documentSubmissionGuide');
};

/**
 * 서류 제출하러 가기: 지연 콜백 제거 후 서류 제출 화면으로 이동 (비밀번호 셋업 미표시)
 */
const handleDocumentSubmissionGuideSubmitDocuments = () => {
  dialogManager.clearPendingAccountPasswordSetup();
  dialogManager.close('documentSubmissionGuide');
  requiredDocumentsVisible.value = true;
};

/**
 * RequiredDocumentsDialog 확인 시 문서 제출 화면으로 이동
 */
const handleRequiredDocumentsConfirm = (_checkedDocuments: unknown) => {
  requiredDocumentsVisible.value = false;
  const userType = LocalStorageService.getItem(LocalStorageKey.MEMBER_TYPE);
  if (userType === 'INDIVIDUAL') {
    router.push({ name: 'individual-document' });
  } else {
    router.push({ name: 'corporate-document' });
  }
};

/**
 * 계좌 개설 다이얼로그 닫기 핸들러
 */
const handleCreateAccountClose = () => {
  dialogManager.close('createAccount');
};

/**
 * 계좌 개설 다이얼로그 확인 핸들러
 */
const handleCreateAccountConfirm = () => {
  dialogManager.close('createAccount');
  router.push({
    name: 'account-management',
    params: { accountManagementTab: 'create' },
    query: { state: 'new' },
  });
};

/**
 * Open API 계좌 필요 다이얼로그 확인(계좌 개설하기) 핸들러
 */
const handleOpenApiAccountNeededConfirm = () => {
  dialogManager.close('openApiAccountNeeded');
  router.push({
    name: 'account-management',
    params: { accountManagementTab: 'create' },
  });
};
</script>
